/**
 * Estrutura do mata-mata da Copa do Mundo 2026 (48 seleções) — chaveamento OFICIAL FIFA.
 * Fonte: FIFA.com (PT-BR) + cruzamento com NBC Sports / Yahoo Sports / Roadtrips.
 *
 * Etapas e jogos (32 jogos no total):
 * - 16 avos / Round of 32: jogos FIFA 73-88 (16 jogos, 32 → 16 times)
 * - Oitavas / Round of 16: jogos FIFA 89-96 (8 jogos)
 * - Quartas: jogos FIFA 97-100 (4 jogos)
 * - Semifinais: jogos FIFA 101-102
 * - Disputa 3º lugar: jogo FIFA 103
 * - Final: jogo FIFA 104
 *
 * Pontuação (multiplicador por fase) — placar exato / acerto do vencedor:
 *   16 avos: 6 / 2
 *   Oitavas: 9 / 3
 *   Quartas: 12 / 4
 *   Semifinal: 15 / 5
 *   Final + 3º lugar: 18 / 6
 *
 * Os "melhores 3ºs" (M3-1..M3-8) ficam atrelados a 8 dos 16 jogos do R32. Os grupos
 * elegíveis para cada slot são os definidos pela FIFA em Anexo C (495 combinações).
 */

import type { GrupoCopaId } from './copaMundo2026';

export type FaseMataMata =
  | '16avos'
  | 'oitavas'
  | 'quartas'
  | 'semi'
  | 'terceiro'
  | 'final';

export interface PontuacaoFase {
  placarExato: number;
  acertoVencedor: number;
}

export const PONTUACAO_POR_FASE: Record<FaseMataMata, PontuacaoFase> = {
  '16avos': { placarExato: 6, acertoVencedor: 2 },
  oitavas: { placarExato: 9, acertoVencedor: 3 },
  quartas: { placarExato: 12, acertoVencedor: 4 },
  semi: { placarExato: 15, acertoVencedor: 5 },
  terceiro: { placarExato: 18, acertoVencedor: 6 },
  final: { placarExato: 18, acertoVencedor: 6 },
};

export const NOME_FASE: Record<FaseMataMata, string> = {
  '16avos': '16 avos de final',
  oitavas: 'Oitavas de final',
  quartas: 'Quartas de final',
  semi: 'Semifinal',
  terceiro: 'Disputa 3º lugar',
  final: 'Final',
};

/**
 * Origem de um lado do jogo do mata-mata.
 * - 'pos-grupo': 1º ou 2º de um grupo específico.
 * - 'melhor-3o': um dos 8 melhores 3ºs (slots 1..8). `gruposElegiveis` lista
 *   os grupos cujo 3º colocado pode preencher esse slot (regra FIFA Annex C).
 * - 'vencedor': vencedor de um jogo anterior do mata-mata.
 * - 'perdedor': perdedor de um jogo anterior (usado na disputa de 3º).
 */
export type OrigemLado =
  | { tipo: 'pos-grupo'; grupo: GrupoCopaId; posicao: 1 | 2 }
  | { tipo: 'melhor-3o'; slot: number; gruposElegiveis: GrupoCopaId[] }
  | { tipo: 'vencedor'; jogoId: string }
  | { tipo: 'perdedor'; jogoId: string };

export interface JogoMataMata {
  id: string;
  fase: FaseMataMata;
  ordem: number;
  ladoA: OrigemLado;
  ladoB: OrigemLado;
  /** Número oficial do jogo na FIFA (73-104). */
  numeroFifa: number;
  /** ISO 8601 com offset BRT (-03:00). Início oficial do jogo. */
  dataHoraBrt: string;
  /** Cidade-sede (FIFA). */
  cidade: string;
  /** Nome do estádio. */
  estadio: string;
}

const grupo = (g: GrupoCopaId, p: 1 | 2): OrigemLado => ({
  tipo: 'pos-grupo',
  grupo: g,
  posicao: p,
});
const terceiro = (slot: number, gruposElegiveis: GrupoCopaId[]): OrigemLado => ({
  tipo: 'melhor-3o',
  slot,
  gruposElegiveis,
});
const venc = (jogoId: string): OrigemLado => ({ tipo: 'vencedor', jogoId });
const perd = (jogoId: string): OrigemLado => ({ tipo: 'perdedor', jogoId });
const iso = (dia: string, hora: string): string => `${dia}T${hora}:00-03:00`;

/**
 * 16 avos / Round of 32 — chaveamento oficial FIFA (jogos 73-88).
 * Slots dos 8 melhores 3ºs:
 *   slot 1 → jogo 74 (1E),  grupos elegíveis: A/B/C/D/F
 *   slot 2 → jogo 77 (1I),  grupos elegíveis: C/D/F/G/H
 *   slot 3 → jogo 79 (1A),  grupos elegíveis: C/E/F/H/I
 *   slot 4 → jogo 80 (1L),  grupos elegíveis: E/H/I/J/K
 *   slot 5 → jogo 81 (1D),  grupos elegíveis: B/E/F/I/J
 *   slot 6 → jogo 82 (1G),  grupos elegíveis: A/E/H/I/J
 *   slot 7 → jogo 85 (1B),  grupos elegíveis: E/F/G/I/J
 *   slot 8 → jogo 87 (1K),  grupos elegíveis: D/E/I/J/L
 */
const JOGOS_16AVOS: JogoMataMata[] = [
  { id: 'mm-16-01', fase: '16avos', ordem: 1,  numeroFifa: 73, ladoA: grupo('A', 2), ladoB: grupo('B', 2),
    dataHoraBrt: iso('2026-06-28', '16:00'), cidade: 'Los Angeles, EUA', estadio: 'SoFi Stadium' },
  { id: 'mm-16-02', fase: '16avos', ordem: 2,  numeroFifa: 74, ladoA: grupo('E', 1), ladoB: terceiro(1, ['A','B','C','D','F']),
    dataHoraBrt: iso('2026-06-29', '17:30'), cidade: 'Boston, EUA', estadio: 'Gillette Stadium' },
  { id: 'mm-16-03', fase: '16avos', ordem: 3,  numeroFifa: 75, ladoA: grupo('F', 1), ladoB: grupo('C', 2),
    dataHoraBrt: iso('2026-06-29', '22:00'), cidade: 'Monterrey, México', estadio: 'Estadio BBVA' },
  { id: 'mm-16-04', fase: '16avos', ordem: 4,  numeroFifa: 76, ladoA: grupo('C', 1), ladoB: grupo('F', 2),
    dataHoraBrt: iso('2026-06-29', '14:00'), cidade: 'Houston, EUA', estadio: 'NRG Stadium' },
  { id: 'mm-16-05', fase: '16avos', ordem: 5,  numeroFifa: 77, ladoA: grupo('I', 1), ladoB: terceiro(2, ['C','D','F','G','H']),
    dataHoraBrt: iso('2026-06-30', '18:00'), cidade: 'Nova York/Nova Jersey, EUA', estadio: 'MetLife Stadium' },
  { id: 'mm-16-06', fase: '16avos', ordem: 6,  numeroFifa: 78, ladoA: grupo('E', 2), ladoB: grupo('I', 2),
    dataHoraBrt: iso('2026-06-30', '14:00'), cidade: 'Dallas, EUA', estadio: 'AT&T Stadium' },
  { id: 'mm-16-07', fase: '16avos', ordem: 7,  numeroFifa: 79, ladoA: grupo('A', 1), ladoB: terceiro(3, ['C','E','F','H','I']),
    dataHoraBrt: iso('2026-06-30', '22:00'), cidade: 'Cidade do México, México', estadio: 'Estadio Azteca' },
  { id: 'mm-16-08', fase: '16avos', ordem: 8,  numeroFifa: 80, ladoA: grupo('L', 1), ladoB: terceiro(4, ['E','H','I','J','K']),
    dataHoraBrt: iso('2026-07-01', '13:00'), cidade: 'Atlanta, EUA', estadio: 'Mercedes-Benz Stadium' },
  { id: 'mm-16-09', fase: '16avos', ordem: 9,  numeroFifa: 81, ladoA: grupo('D', 1), ladoB: terceiro(5, ['B','E','F','I','J']),
    dataHoraBrt: iso('2026-07-01', '21:00'), cidade: 'Santa Clara, EUA', estadio: "Levi's Stadium" },
  { id: 'mm-16-10', fase: '16avos', ordem: 10, numeroFifa: 82, ladoA: grupo('G', 1), ladoB: terceiro(6, ['A','E','H','I','J']),
    dataHoraBrt: iso('2026-07-01', '17:00'), cidade: 'Seattle, EUA', estadio: 'Lumen Field' },
  { id: 'mm-16-11', fase: '16avos', ordem: 11, numeroFifa: 83, ladoA: grupo('K', 2), ladoB: grupo('L', 2),
    dataHoraBrt: iso('2026-07-02', '20:00'), cidade: 'Toronto, Canadá', estadio: 'BMO Field' },
  { id: 'mm-16-12', fase: '16avos', ordem: 12, numeroFifa: 84, ladoA: grupo('H', 1), ladoB: grupo('J', 2),
    dataHoraBrt: iso('2026-07-02', '16:00'), cidade: 'Los Angeles, EUA', estadio: 'SoFi Stadium' },
  { id: 'mm-16-13', fase: '16avos', ordem: 13, numeroFifa: 85, ladoA: grupo('B', 1), ladoB: terceiro(7, ['E','F','G','I','J']),
    dataHoraBrt: iso('2026-07-03', '00:00'), cidade: 'Vancouver, Canadá', estadio: 'BC Place' },
  { id: 'mm-16-14', fase: '16avos', ordem: 14, numeroFifa: 86, ladoA: grupo('J', 1), ladoB: grupo('H', 2),
    dataHoraBrt: iso('2026-07-03', '19:00'), cidade: 'Miami, EUA', estadio: 'Hard Rock Stadium' },
  { id: 'mm-16-15', fase: '16avos', ordem: 15, numeroFifa: 87, ladoA: grupo('K', 1), ladoB: terceiro(8, ['D','E','I','J','L']),
    dataHoraBrt: iso('2026-07-03', '22:30'), cidade: 'Kansas City, EUA', estadio: 'Arrowhead Stadium' },
  { id: 'mm-16-16', fase: '16avos', ordem: 16, numeroFifa: 88, ladoA: grupo('D', 2), ladoB: grupo('G', 2),
    dataHoraBrt: iso('2026-07-03', '15:00'), cidade: 'Dallas, EUA', estadio: 'AT&T Stadium' },
];

/** Oitavas / Round of 16 — jogos FIFA 89-96 (chaveamento oficial). */
const JOGOS_OITAVAS: JogoMataMata[] = [
  { id: 'mm-oit-1', fase: 'oitavas', ordem: 1, numeroFifa: 89,
    ladoA: venc('mm-16-02'), ladoB: venc('mm-16-05'),
    dataHoraBrt: iso('2026-07-04', '21:00'), cidade: 'Filadélfia, EUA', estadio: 'Lincoln Financial Field' },
  { id: 'mm-oit-2', fase: 'oitavas', ordem: 2, numeroFifa: 90,
    ladoA: venc('mm-16-01'), ladoB: venc('mm-16-03'),
    dataHoraBrt: iso('2026-07-04', '14:00'), cidade: 'Houston, EUA', estadio: 'NRG Stadium' },
  { id: 'mm-oit-3', fase: 'oitavas', ordem: 3, numeroFifa: 91,
    ladoA: venc('mm-16-04'), ladoB: venc('mm-16-06'),
    dataHoraBrt: iso('2026-07-05', '17:00'), cidade: 'Nova York/Nova Jersey, EUA', estadio: 'MetLife Stadium' },
  { id: 'mm-oit-4', fase: 'oitavas', ordem: 4, numeroFifa: 92,
    ladoA: venc('mm-16-07'), ladoB: venc('mm-16-08'),
    dataHoraBrt: iso('2026-07-05', '21:00'), cidade: 'Cidade do México, México', estadio: 'Estadio Azteca' },
  { id: 'mm-oit-5', fase: 'oitavas', ordem: 5, numeroFifa: 93,
    ladoA: venc('mm-16-11'), ladoB: venc('mm-16-12'),
    dataHoraBrt: iso('2026-07-06', '17:00'), cidade: 'Dallas, EUA', estadio: 'AT&T Stadium' },
  { id: 'mm-oit-6', fase: 'oitavas', ordem: 6, numeroFifa: 94,
    ladoA: venc('mm-16-09'), ladoB: venc('mm-16-10'),
    dataHoraBrt: iso('2026-07-06', '21:00'), cidade: 'Seattle, EUA', estadio: 'Lumen Field' },
  { id: 'mm-oit-7', fase: 'oitavas', ordem: 7, numeroFifa: 95,
    ladoA: venc('mm-16-14'), ladoB: venc('mm-16-16'),
    dataHoraBrt: iso('2026-07-07', '13:00'), cidade: 'Atlanta, EUA', estadio: 'Mercedes-Benz Stadium' },
  { id: 'mm-oit-8', fase: 'oitavas', ordem: 8, numeroFifa: 96,
    ladoA: venc('mm-16-13'), ladoB: venc('mm-16-15'),
    dataHoraBrt: iso('2026-07-07', '17:00'), cidade: 'Vancouver, Canadá', estadio: 'BC Place' },
];

/** Quartas — jogos FIFA 97-100. */
const JOGOS_QUARTAS: JogoMataMata[] = [
  { id: 'mm-qua-1', fase: 'quartas', ordem: 1, numeroFifa: 97,
    ladoA: venc('mm-oit-1'), ladoB: venc('mm-oit-2'),
    dataHoraBrt: iso('2026-07-09', '17:00'), cidade: 'Boston, EUA', estadio: 'Gillette Stadium' },
  { id: 'mm-qua-2', fase: 'quartas', ordem: 2, numeroFifa: 98,
    ladoA: venc('mm-oit-5'), ladoB: venc('mm-oit-6'),
    dataHoraBrt: iso('2026-07-10', '16:00'), cidade: 'Los Angeles, EUA', estadio: 'SoFi Stadium' },
  { id: 'mm-qua-3', fase: 'quartas', ordem: 3, numeroFifa: 99,
    ladoA: venc('mm-oit-3'), ladoB: venc('mm-oit-4'),
    dataHoraBrt: iso('2026-07-11', '18:00'), cidade: 'Miami, EUA', estadio: 'Hard Rock Stadium' },
  { id: 'mm-qua-4', fase: 'quartas', ordem: 4, numeroFifa: 100,
    ladoA: venc('mm-oit-7'), ladoB: venc('mm-oit-8'),
    dataHoraBrt: iso('2026-07-11', '22:00'), cidade: 'Kansas City, EUA', estadio: 'Arrowhead Stadium' },
];

/** Semifinais — jogos FIFA 101-102. */
const JOGOS_SEMI: JogoMataMata[] = [
  { id: 'mm-sem-1', fase: 'semi', ordem: 1, numeroFifa: 101,
    ladoA: venc('mm-qua-1'), ladoB: venc('mm-qua-2'),
    dataHoraBrt: iso('2026-07-14', '16:00'), cidade: 'Dallas, EUA', estadio: 'AT&T Stadium' },
  { id: 'mm-sem-2', fase: 'semi', ordem: 2, numeroFifa: 102,
    ladoA: venc('mm-qua-3'), ladoB: venc('mm-qua-4'),
    dataHoraBrt: iso('2026-07-15', '16:00'), cidade: 'Atlanta, EUA', estadio: 'Mercedes-Benz Stadium' },
];

/** Disputa do 3º lugar — jogo FIFA 103. */
const JOGO_TERCEIRO: JogoMataMata = {
  id: 'mm-3lugar',
  fase: 'terceiro',
  ordem: 1,
  numeroFifa: 103,
  ladoA: perd('mm-sem-1'),
  ladoB: perd('mm-sem-2'),
  dataHoraBrt: iso('2026-07-18', '18:00'),
  cidade: 'Miami, EUA',
  estadio: 'Hard Rock Stadium',
};

/** Final — jogo FIFA 104. */
const JOGO_FINAL: JogoMataMata = {
  id: 'mm-final',
  fase: 'final',
  ordem: 1,
  numeroFifa: 104,
  ladoA: venc('mm-sem-1'),
  ladoB: venc('mm-sem-2'),
  dataHoraBrt: iso('2026-07-19', '16:00'),
  cidade: 'Nova York/Nova Jersey, EUA',
  estadio: 'MetLife Stadium',
};

export const JOGOS_MATA_MATA: JogoMataMata[] = [
  ...JOGOS_16AVOS,
  ...JOGOS_OITAVAS,
  ...JOGOS_QUARTAS,
  ...JOGOS_SEMI,
  JOGO_TERCEIRO,
  JOGO_FINAL,
];

export const FASES_MATA_MATA: FaseMataMata[] = [
  '16avos',
  'oitavas',
  'quartas',
  'semi',
  'terceiro',
  'final',
];

/** Ordem cronológica (data/hora BRT) — usada na UI e impressão. */
export function compararJogosMataMataCronologico(a: JogoMataMata, b: JogoMataMata): number {
  const msA = Date.parse(a.dataHoraBrt);
  const msB = Date.parse(b.dataHoraBrt);
  if (msA !== msB) return msA - msB;
  return a.numeroFifa - b.numeroFifa;
}

export function jogosDaFase(fase: FaseMataMata): JogoMataMata[] {
  return JOGOS_MATA_MATA.filter((j) => j.fase === fase).sort(compararJogosMataMataCronologico);
}

/** Busca um jogo do mata-mata pelo ID. */
export function jogoMataMataPorId(id: string): JogoMataMata | undefined {
  return JOGOS_MATA_MATA.find((j) => j.id === id);
}

/** Retorna o ms BRT de início do jogo. Se não houver, retorna `Infinity`. */
export function inicioJogoMataMataMs(id: string): number {
  const jogo = jogoMataMataPorId(id);
  if (!jogo) return Infinity;
  const ms = Date.parse(jogo.dataHoraBrt);
  return Number.isFinite(ms) ? ms : Infinity;
}

/** True se a hora atual já passou do início do jogo (palpite individual encerrado). */
export function jogoMataMataJaIniciou(id: string, agoraMs: number = Date.now()): boolean {
  return agoraMs >= inicioJogoMataMataMs(id);
}
